<template>
   <!--Page alert + loader-->
   <PageStatus :alert="defaultAlert" :table-data="tableData" />

   
      <!--Import Data-->
   <ImportModal
      :form-import-data="formImportData"
      :alert="defaultAlert"
      template-url="/import-templates/users/usersExport.csv"
      @file-change="onFileImportChange"
      @import="importRecords"
   >
      <!--Import Notes-->
      <div id="accordion">
         <div class="card">
            <div class="card-header">
               <a class="btn" data-bs-toggle="collapse" href="#collapseOne">
                  Import Process
               </a>
            </div>
            <div id="collapseOne" class="collapse show" data-bs-parent="#accordion">
               <div class="card-body">
                  <ol>
                     <li>
                        Make sure you have finished importing
                        <b>companies & branches</b>.
                        If not, go to those modules and import
                        <b>companies first, then branches</b>,
                        then come back and import users.
                     </li>
                     <li>
                        Make sure every company has a branch before importing.
                        The <b>Legacy Company ID</b> and
                        <b>Legacy Branch ID</b> in the Excel file are used
                        to automatically find the corresponding company and
                        branch in the new system.
                     </li>
                     <li>
                        Download the above Excel file, open it, and first
                        understand the following columns:
                        <ul>
                           <li>
                              <b>Legacy Source -</b>
                              Name of the software you are importing the
                              users from, e.g., QuickBooks or Sage.
                              <b>(NB: Use one word only for the software name.)</b>
                           </li>
                           <li>
                              <b>Legacy ID -</b>
                              Represents the old
                              <b>users</b> table's auto-increment ID.
                           </li>
                           <li>
                              <b>Legacy Company ID -</b>
                              Represents the company ID from the old system
                              that the user belongs to.
                           </li>
                           <li>
                              <b>Legacy Branch ID -</b>
                              Represents the branch ID from the old system
                              that the user belongs to.
                           </li>
                           <li>
                              <b>User ID -</b>
                              Represents the old system's user ID.
                              <b>NB:</b> This value is not imported as the
                              new system generates its own user ID.
                           </li>
                           <li>
                              <b>Other Legacy ID columns -</b>
                              If available, these represent IDs of other
                              tables that the users table is connected to
                              and form relationships.
                           </li>
                           <li>
                              <b>NB:</b>
                              Legacy ID columns are used by the code to find
                              the corresponding IDs in the new system.
                              Company and branch IDs are mapped
                              automatically during import.
                           </li>
                        </ul>
                     </li>
                     <li>
                        Download the users data from the old system,
                        e.g., QuickBooks or Sage, open it, and arrange the
                        data according to the above import template before
                        importing.
                     </li>
                  </ol>
               </div>
            </div>
         </div>
      </div>
      <!--End Import Notes-->
   </ImportModal>
   <!--Import Data-->


      <!--Filter Data Modal (shared: FilterModal.vue)-->
   <FilterModal
      :draft="filterDraft"
      v-model:search-by="tableData.searchBy"
      :view-options="viewOptions"
      :search-by-options="searchByOptions"
      :alert="defaultAlert"
      @search-by-change="changePlaceholder()"
      @apply="applyFilters"
      @clear="clearFilters"
   />
   <!--End Filter Data Modal-->

<!--Form Modal-->
<RecordFormModal
   :title="isEditing ? 'Update Company & Branch Access' : 'Assign Company & Branch Access'"
   title-icon="fa fa-sitemap"
   size="xl"
   :is-editing="isEditing"
   :is-saving="isSaving"
   :alert="defaultAlert"
   :show-reset="false"
   @save="saveOrEditRecord"
   @sample="addSampleData"
>
   <!-- Who: pick a user (Add) or show the user's name (Edit) -->
   <div class="row g-3 mb-2">
      <SelectField
         v-if="!isEditing"
         v-model="selectedAccessUserId"
         col="col-md-6"
         name="access_user"
         label="User"
         icon="fa fa-user"
         :options="usersOptions"
         :reduce="user => user.id"
         option-label="label"
         placeholder="Search and select a user"
         tooltip="Pick the user whose access you are setting"
         :submitted="submitted"
         required
         @change="onAccessUserSelected"
      />
      <div v-else class="col-md-12">
         <h5 class="sa-core-color mt-1 mb-0">
            {{ formInputs.first_name }} {{ formInputs.middle_name }} {{ formInputs.last_name }}
         </h5>
      </div>
      <div class="col-md-12">
         <small class="text-muted sa-core-color">(Expand a company to see its branches i.e., available in left & assigned in right)</small>
      </div>
   </div>

   <!-- Three Column Layout -->
   <div class="row g-3 align-items-stretch">

      <!-- Available -->
      <div class="col-md-5">
         <div class="card bg-sa-card h-100">
            <div class="card-header bg-sa-card-header">
               <strong>Available</strong>
            </div>

            <div
               class="card-body bg-sa-card-body p-2"
               style="height: 450px; overflow-y: auto;"
            >
               <div
                  v-for="company in availableCompanies"
                  :key="company.id"
                  class="mb-2"
               >
                  <!-- Company -->
                  <div class="d-flex align-items-center" :class="{ 'expanded-item': isAvailableCompanyExpanded(company.id) }">
                     <button
                        type="button"
                        class="btn btn-sm btn-link p-0 me-1"
                        @click="toggleAvailableCompanyExpanded(company.id)"
                     >
                        <i
                           :class="[
                              'fa',
                              isAvailableCompanyExpanded(company.id)
                                 ? 'fa-chevron-down'
                                 : 'fa-chevron-right'
                           ]"
                        ></i>
                     </button>

                     <input
                        type="checkbox"
                        class="form-check-input me-2"
                        :checked="isAvailableCompanyFullySelected(company)"
                        :indeterminate="isAvailableCompanyPartiallySelected(company)"
                        @change="toggleAvailableCompany(company)"
                     />

                     <span class="fw-semibold">
                        {{ company.company_name }}
                     </span>
                  </div>

                  <!-- Branches -->
                  <Transition name="expand-fade">
                     <div
                        v-if="isAvailableCompanyExpanded(company.id)"
                        class="ms-4 mt-1 expanded-branches"
                     >
                        <div
                           v-for="branch in company.branches"
                           :key="branch.id"
                           class="form-check mb-1"
                        >
                           <input
                              :id="'available-branch-' + branch.id"
                              type="checkbox"
                              class="form-check-input"
                              :checked="isAvailableBranchSelected(company.id, branch.id)"
                              @change="toggleAvailableBranch(company, branch)"
                           />
                           <label
                              :for="'available-branch-' + branch.id"
                              class="form-check-label"
                           >
                              {{ branch.branch_name }}
                           </label>
                        </div>
                     </div>
                  </Transition>
               </div>

               <div
                  v-if="availableCompanies.length === 0"
                  class="text-center text-muted py-5 sa-core-color"
               >
                  No available companies or branches.
               </div>
            </div>
         </div>
      </div>

      <!-- Arrows -->
      <div class="col-md-2 d-flex flex-column justify-content-center align-items-center">
         <button
            type="button"
            class="btn btn-primary mb-3"
            @click="moveSelectedToAssigned"
            :disabled="!hasAvailableSelection"
            title="Move selected to assigned"
         >
            <i class="fa fa-arrow-right"></i>
         </button>

         <button
            type="button"
            class="btn btn-secondary"
            @click="moveSelectedToAvailable"
            :disabled="!hasAssignedSelection"
            title="Move selected back to available"
         >
            <i class="fa fa-arrow-left"></i>
         </button>
      </div>

      <!-- Assigned -->
      <div class="col-md-5">
         <div class="card bg-sa-card h-100">
            <div class="card-header bg-sa-card-header">
               <strong>Assigned</strong>
            </div>

            <div
               class="card-body bg-sa-card-body p-2"
               style="height: 450px; overflow-y: auto;"
            >
               <div
                  v-for="company in assignedCompanies"
                  :key="company.id"
                  class="mb-2"
               >
                  <!-- Company -->
                  <div class="d-flex align-items-center" :class="{ 'expanded-item': isAssignedCompanyExpanded(company.id) }">
                     <button
                        type="button"
                        class="btn btn-sm btn-link p-0 me-1"
                        @click="toggleAssignedCompanyExpanded(company.id)"
                     >
                        <i
                           :class="[
                              'fa',
                              isAssignedCompanyExpanded(company.id)
                                 ? 'fa-chevron-down'
                                 : 'fa-chevron-right'
                           ]"
                        ></i>
                     </button>

                     <input
                        type="checkbox"
                        class="form-check-input me-2"
                        :checked="isAssignedCompanyFullySelected(company)"
                        :indeterminate="isAssignedCompanyPartiallySelected(company)"
                        @change="toggleAssignedCompany(company)"
                     />

                     <span class="fw-semibold">
                        {{ company.company_name }}
                     </span>
                  </div>

                  <!-- Branches -->
                  <Transition name="expand-fade">
                     <div
                        v-if="isAssignedCompanyExpanded(company.id)"
                        class="ms-4 mt-1 expanded-branches"
                     >
                        <div
                           v-for="branch in company.branches"
                           :key="branch.id"
                           class="form-check mb-1"
                        >
                           <input
                              :id="'assigned-branch-' + branch.id"
                              type="checkbox"
                              class="form-check-input"
                              :checked="isAssignedBranchSelected(company.id, branch.id)"
                              :disabled="isProtectedHeadOffice(company, branch)"
                              @change="toggleAssignedBranch(company, branch)"
                           />
                           <label
                              :for="'assigned-branch-' + branch.id"
                              class="form-check-label"
                           >
                              {{ branch.branch_name }}
                              <i v-if="isProtectedHeadOffice(company, branch)" class="fa fa-lock ms-1 opacity-75" title="The owner's Head Office: always kept"></i>
                           </label>
                        </div>
                     </div>
                  </Transition>
               </div>

               <div
                  v-if="assignedCompanies.length === 0"
                  class="text-center text-muted py-5 sa-core-color"
               >
                  No company or branch access assigned.
               </div>
            </div>
         </div>
      </div>

   </div>
</RecordFormModal>
<!--End Form Modal-->


   <h4 class="sa-core-color fw-semibold mb-2">
      <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
   </h4>
   
<!--Tables-->
<div class="card glass mt-0 bg-sa-card">
    <div class="card-body bg-sa-card-body p-0">

      <!-- Toggleable Tabs -->
        <ul class="nav nav-tabs app-tabs" role="tablist">
            <li class="nav-item" role="presentation">
                <router-link
                    to="/user-management/users/"
                    class="nav-link"
                    :class="{ 'app-tab-active': activeTab === 'users' }"
                    @click="activeTab = 'users'"
                >
                    <span class="sa-core-color">Users</span>
                </router-link>
            </li>

            <li class="nav-item" role="presentation">
                <router-link
                    to="/user-management/user_company_branch_access/"
                    class="nav-link"
                    :class="{ 'app-tab-active': activeTab === 'user_company_branch_access' }"
                    @click="activeTab = 'user_company_branch_access'"
                >
                    <span class="sa-core-color">Company & Branch Access</span>
                </router-link>
            </li>
        </ul>
        <!-- End Toggleable Tabs -->

                <!-- Table action row (shared: TableToolbar.vue) -->
        <TableToolbar
            can-add
            :filter-count="activeFilterCount"
            :columns="tableColumns"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="tableData.selectedRecordIds.length"
            :deleted-view="tableData.recordFilter === 'deletedone'"
            :can-delete="$hasPermission('users.delete')"
            @add="clearForm()"
            @filter="openFilters"
            @clear-selection="clearSelection"
            @bulk-action="runBulkAction"
        >
            <!-- More menu -->
            <template #more>
                <li><h6 class="dropdown-header">Data</h6></li>
                <li>
                    <button type="button" class="dropdown-item" data-bs-toggle="modal" data-bs-target="#importModal">
                        <i class="fa fa-upload fa-fw me-1"></i> Import
                    </button>
                </li>
                <li>
                    <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                        <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                    </button>
                </li>
                <li>
                    <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                        <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                    </button>
                </li>
                <li>
                    <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                        <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                    </button>
                </li>
            </template>
        </TableToolbar>
        <!-- Table Search Panel -->

        <!-- Table Search Panel -->
        <SearchBox
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
        />
        <!-- What the table is currently showing -->
        <ActiveFilters
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
        />
        <!-- Table -->
        <div class="table-responsive">
            
          <table
    class="table table-hover align-middle mb-0"
    id="bootstrapTable"
    tabindex="0"
    @keydown.up="$moveRowSelection(-1, $event)"
    @keydown.down="$moveRowSelection(1, $event)"
    @keydown.enter="$onTableEnter($event)"
>
      <thead>
      <tr>
         <th>
            <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
         </th>
         <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
         <th class="button hide-sm-td">No</th>
         <SortableTh v-if="isColumnVisible('first_name')" column="first_name" :table-data="tableData" @sort="orderBy">User</SortableTh>
         <SortableTh v-if="isColumnVisible('email')" column="email" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Email</SortableTh>
         <SortableTh v-if="isColumnVisible('role')" column="role" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Role</SortableTh>
         <th v-if="isColumnVisible('access')" class="hide-sm-td">Access</th>
         <SortableTh v-if="isColumnVisible('is_active')" column="is_active" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Status</SortableTh>
         <th class="button">Action</th>
      </tr>
   </thead>
   <tbody>
      <template v-for="(record, index) in tableData.records" :key="record.id">

         <!-- Main row -->
         <tr class="clickable-row"
    :data-record-id="record.id"
    :class="[
       { 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) },
       record.pinned_role ? 'pinned-row pinned-' + record.pinned_role : ''
    ]"
    @click="$onRowClick(record, $event)"
    @dblclick="$onRowDblClick(record, $event)"
>
            <td>
               <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
            </td>
            <td class="expand-cell hide-sm-td"
                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                @click="$toggleDetails(record.id)">
               <i class="fa fa-lg expand-icon ms-1"
                  :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                  :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
            </td>
            <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
            <td v-if="isColumnVisible('first_name')">
   <!-- Phones: + / − and the number go in front of the user (their columns are hidden) -->
   <span
      class="show-sm-inline expand-cell me-2"
      :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
      @click="$toggleDetails(record.id)"
   >
      <i class="fa fa-lg expand-icon"
         :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
         :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
   </span>
   <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>
   <img v-if="record.file_path" :src="`${$baseUrl}/users/${record.file_path}`" height="24" class="rounded-circle me-1" />
   {{ getFullName(record) }}
   <span v-if="record.pinned_role === 'system_admin'" class="badge bg-warning text-dark ms-1">
      <i class="fa fa-shield me-1"></i>System Admin
   </span>
   <span v-else-if="record.pinned_role === 'developer'" class="badge bg-info text-dark ms-1">
      <i class="fa fa-code me-1"></i>Developer
   </span>
</td>
            <td v-if="isColumnVisible('email')" class="hide-sm-td">{{ record.email }}</td>
            <td v-if="isColumnVisible('role')" class="hide-sm-td">{{ getRoleLabel(record.role) }}</td>
            <td v-if="isColumnVisible('access')" class="hide-sm-td">
               <span :class="record.access_branch_count ? 'badge bg-info text-dark' : 'badge bg-warning text-dark'">
                  {{ record.access_branch_count || 0 }} branches · {{ record.access_company_count || 0 }} companies
               </span>
            </td>
            <td v-if="isColumnVisible('is_active')" class="hide-sm-td">
               <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-secondary'">
                  {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
               </span>
            </td>
            <td>
               <RowActions
                  :record="record"
                  :modal-id="'deleteCrudModal' + record.id"
                  edit-label="Company & Branch Access"
                  edit-icon="fa fa-building"
                  :can-soft-delete="$hasPermission('users.delete')"
                  :can-restore="$hasPermission('users.delete')"
                  :can-delete-permanently="$hasPermission('users.delete')"
                  @edit="openCompanyBranchAccess"
                  @delete-type="setDeleteType"
               />

               <!--Delete Modal-->
               <DeleteModal
                  :modal-id="'deleteCrudModal' + record.id"
                  :title="deleteModalTitle"
                  :message="deleteModalMessage"
                  @confirm="deleteRecord(record.id)"
               />
               <!--End Delete Modal-->
            </td>
         </tr>

         <!-- Hidden expanded row -->
         <tr class="collapse-row" :class="record.pinned_role ? 'pinned-row pinned-' + record.pinned_role : ''">
            <td class="p-0"></td>
            <td class="p-0 hide-sm-td"></td>
            <td colspan="7" class="p-0">
               <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                  <div class="p-3 details-list">
                     <div><i class="fa fa-hashtag"></i><strong>User Number:</strong> {{ record.user_number }}</div>
                     <div><i class="fa fa-id-badge"></i><strong>Username:</strong> {{ record.username }}</div>
                     <div><i class="fa fa-building"></i><strong>Home Company:</strong> {{ record.company_name }}</div>
                     <div><i class="fa fa-sitemap"></i><strong>Home Branch:</strong> {{ record.branch_name }}</div>

                     <!-- Full access list, grouped by company -->
                     <div>
                        <i class="fa fa-key"></i><strong>Access:</strong>
                        <div v-for="(branches, companyName) in groupAccessByCompany(record.access_branches)"
                             :key="companyName"
                             class="mt-2">
                           <div class="fw-semibold text-info small">{{ companyName }} ({{ branches.length }})</div>
                           <div class="d-flex flex-wrap gap-1 mt-1">
                              <span v-for="b in branches" :key="b.branch_id"
                                    :class="Number(b.is_default) === 1 ? 'badge bg-primary' : 'badge bg-secondary'"
                                    :title="Number(b.is_default) === 1 ? 'Default branch' : ''">
                                 <i v-if="Number(b.is_default) === 1" class="fa fa-star me-1"></i>{{ b.branch_name }}
                              </span>
                           </div>
                        </div>
                        <span v-if="!record.access_branch_count" class="text-warning">No branch access. This user cannot work in any branch.</span>
                     </div>

                     <!-- Shown on phones, or always when the column is hidden (Columns button) -->
                     <div :class="{ 'show-sm-td': isColumnVisible('first_name') }"><i class="fa fa-user"></i><strong>User:</strong> {{ getFullName(record) }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('email') }"><i class="fa fa-envelope"></i><strong>Email:</strong> {{ record.email }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('role') }"><i class="fa fa-users"></i><strong>Role:</strong> {{ getRoleLabel(record.role) }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('is_active') }">
                        <i class="fa fa-toggle-on"></i><strong>Status:</strong>
                        <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-secondary'">
                           {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
                        </span>
                     </div>

                     <!-- Always in details -->
                     <div><i class="fa fa-phone"></i><strong>Phone:</strong> {{ record.phone }}</div>
                     <div><i class="fa fa-venus-mars"></i><strong>Gender:</strong> {{ getGenderLabel(record.gender) }}</div>
                     <div><i class="fa fa-birthday-cake"></i><strong>Date of Birth:</strong> {{ record.date_of_birth }}</div>
                     <div v-if="$hasPermission('view-location')"><i class="fa fa-location-arrow"></i><strong>Created From:</strong> {{ record.current_location }}</div>

                     <div><i class="fa fa-user"></i><strong>Created By:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
                     <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ this.$ConvertToLocalDate(record.date_created) }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ this.$ConvertToLocalDate(record.date_updated) }}</div>
                     <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                  </div>
               </div>
            </td>
         </tr>

      </template>

      <!-- No rows: blank while loading, then the empty message in the middle -->
      <tr v-if="tableData.records.length === 0" class="table-filler">
         <td colspan="9" class="text-center align-middle" :style="{ height: fullPageHeight() }">
            <TableEmptyState
               v-if="!tableLoading"
               :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
               :can-add="true"
               :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No users yet'"
               :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Add users on the Users tab, then give them company and branch access here.'"
               icon="fa fa-sitemap"
               @add="clearForm()"
               @clear="clearAllFilterChips"
            />
         </td>
      </tr>
   </tbody>

   <tfoot class="table-light">
      <tr>
         <td colspan="9">
            <!-- Pagination (hidden when there are no rows; its space is kept) -->
            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
            <Pagination
               v-else
               :currentPage="tableData.currentPage"
               :totalPages="tableData.totalPages"
               :hasNextPage="tableData.hasNextPage"
               :totalRecords="tableData.totalRecords"
               :startRecord="tableData.startRecord"
               :endRecord="tableData.endRecord"
               :showFullPagination="tableData.totalRecords > 0"
               :lastKnownPage="lastKnownPage"
               @page-changed="fetchRecords"
               @loaderMessage="updateLoaderMessage"
               @last-page-request="goToLastPage"
            />
         </td>
      </tr>
   </tfoot>
</table>
        </div>
    </div>
</div>
<!--End Tables-->

</template>
<script>
import PageStatus from "../../../components/admin/PageStatus.vue";
import TableToolbar from "../../../components/admin/TableToolbar.vue";
import FilterModal from "../../../components/admin/FilterModal.vue";
import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
import Pagination from "../../../components/admin/Pagination.vue";
import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
import SortableTh from "../../../components/admin/SortableTh.vue";
import DeleteModal from "../../../components/admin/DeleteModal.vue";
import RowActions from "../../../components/admin/RowActions.vue";
import SearchBox from "../../../components/admin/SearchBox.vue";
import ImportModal from "../../../components/admin/ImportModal.vue";
import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
import FormField from "../../../components/admin/FormField.vue";
import SelectField from "../../../components/admin/SelectField.vue";
import userCompanyBranchAccessMixin from "./mixins/userCompanyBranchAccessMixin.js";
import tableMixin from "@/mixins/tableMixin.js";
import axios from 'axios';


export default {
   name: 'UserCompanyBranchAccess',
   mixins: [tableMixin, userCompanyBranchAccessMixin], // shared table methods first, then this page's own
   components: {
      PageStatus,
      TableToolbar,
      FilterModal,
      ActiveFilters,
      Pagination,
      TableEmptyState,
      SortableTh,
      DeleteModal,
      RowActions,
      SearchBox,
      ImportModal,
      RecordFormModal,
      FormField,
      SelectField,
   },
      data() {
      return {
         // Standard data (isSaving, tableData, defaultAlert, formImportData ...) comes from tableMixin
         standardPageSetup: true,          // tableMixin sets up Enter-to-save and table shortcuts
         apiUrl: "/users/api_users.php",   // used by tableMixin's fetchRecords, delete, import, export
         exportFileName: "usersExport",    // name of the exported file
         columnsKey: "usercompanybranchaccess", // saves this page's hidden columns (Columns button)
         activeTab: 'user_company_branch_access',
         usersOptions: [],
         formInputs: this.getDefaultFormInputs(),

         // Company & Branch Access
         availableCompanies: [],
         assignedCompanies: [],
         availableSelectedBranches: {},
         assignedSelectedBranches: {},
         expandedAvailableCompanies: [],
         expandedAssignedCompanies: [],
         selectedAccessUserId: '',

         rolesOptions: [],
         pageIcon: 'fa fa-sitemap', // same icon as this page in the sidebar
         sexOptions: [
            { label: "Male", value: 1 },
            { label: "Female", value: 0 }
         ],
         // Filter window: View
         viewOptions: [
            { label: "All Records", value: "all" },
            { label: "Records By Me", value: "byme" },
            { label: "Records Deleted", value: "deletedone" },
         ],
         // Filter window: Search By
         searchByOptions: [
            { label: "First Name", value: "first_name" },
            { label: "Last Name", value: "last_name" },
            { label: "Email", value: "email" },
            { label: "Phone", value: "phone" },
            { label: "Username", value: "username" },
         ],
      };
   },
   mounted(){
      this.fetchRolesOptions(); // role names for the table
      this.fetchUsersOptions(); // users for "Select a user"
   },
   async created() {      
      //alert(this.$store.state.isLoggedIn);
      //this.retriveUserDetails();           
      await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
      this.$checkIfCanFetchLocation();//Only applies if location is enabled for user. If not Log user out for him to enable location 
   },
   computed: {   
   // Page heading that follows the chosen filter.
      pageTitle() {
          const titles = {
              all: "Companies and Branches",
              byme: "Companies and Branches By Me",
              deletedone: "Companies and Branches Deleted Records",
          };
          return titles[this.tableData.recordFilter] || "Companies and Branches";
      },

           // Columns the user can show or hide (Columns button)
      tableColumns() {
          return [
              { key: "first_name", label: "User" },
              { key: "email", label: "Email" },
              { key: "role", label: "Role" },
              { key: "access", label: "Access" },
              { key: "is_active", label: "Status" },
          ];
      },
      hasAvailableSelection() {
          return Object.values(this.availableSelectedBranches)
              .some(value => value === true);
      },
      hasAssignedSelection() {
          return Object.values(this.assignedSelectedBranches)
              .some(value => value === true);
      },
   },
   methods: {
      getFullName(record) {
         return [record.first_name, record.middle_name, record.last_name].filter(Boolean).join(" ");
      },
      // [{company_name, branch_name, ...}] → { "Company A": [...branches], "Company B": [...] }
      groupAccessByCompany(list) {
         const groups = {};
         (list || []).forEach(item => {
            (groups[item.company_name] ??= []).push(item);
         });
         return groups;
      },
          // Load the users for the "Select a user" dropdown.
      fetchUsersOptions() {
          axios
              .get(`${this.apiUrl}?action=fetchUsersOptions`, this.$handleConfig())
              .then((response) => {
                  if (response.data.status === "success") {
                      this.usersOptions = (response.data.results ?? []).map((u) => ({
                          id: Number(u.id),
                          label: [u.first_name, u.middle_name, u.last_name].filter(Boolean).join(" "),
                      }));
                  }
              })
              .catch(() => {});
      },



      // A user was picked: load what they already have (right) and what's left (left).
      onAccessUserSelected(userId) {
          this.availableSelectedBranches = {};
          this.assignedSelectedBranches = {};
          this.expandedAvailableCompanies = [];
          this.expandedAssignedCompanies = [];

          if (!userId) {
              this.availableCompanies = [];
              this.assignedCompanies = [];
              return;
          }

          this.loadCompanyBranchAccess(userId);
      },
      isProtectedHeadOffice(company, branch) {
          return (
              Number(company.is_company_owner) === 1 &&
              Number(branch.is_head_office) === 1
          );
      },
      fetchRolesOptions(){
         let url = `${this.apiUrl}?action=fetchRoles`;

         axios.get(url,this.$handleConfig())
            .then((response) => {
               if (response.data.status=="success") {
                  this.handleFetchRolesSuccess(response);
               } else {
                  this.handleAlert("starting",response.data.message,"danger","page");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","page");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","page");
                }
               
            });
      },
            // The access window only needs who the user is
      getDefaultFormInputs(){
         return {
            id: "",
            first_name: "",
            middle_name: "",
            last_name: "",
         };
      },
      // Table defaults: the standard ones (tableMixin) + this page's own
      getDefaultTableData(){
         return {
            ...this.baseTableData(),
            searchBy: 'first_name',
            searchPlaceHolderText: 'Search by First Name',
         };
      },
      async addSampleData() {
         if (!this.usersOptions.length) {
            this.handleAlert("ending", "No users loaded to use as a sample.", "danger", "modal");
            return;
         }

         // Pick the first user. Setting the v-model in code doesn't fire
         // @update:modelValue, so load their access directly.
         this.selectedAccessUserId = this.usersOptions[0].id;
         this.availableSelectedBranches = {};
         this.assignedSelectedBranches = {};
         this.expandedAvailableCompanies = [];
         this.expandedAssignedCompanies = [];
         await this.loadCompanyBranchAccess(this.selectedAccessUserId);

         // Move the first available branch to Assigned
         const company = this.availableCompanies.find(c => c.branches && c.branches.length);
         if (!company) return; // user already has every branch

         const branch = company.branches[0];

         company.branches = company.branches.filter(b => b.id !== branch.id);
         if (!company.branches.length) {
            this.availableCompanies = this.availableCompanies.filter(c => c.id !== company.id);
         }

         const existing = this.assignedCompanies.find(c => c.id === company.id);
         if (existing) {
            existing.branches.push(branch);
         } else {
            this.assignedCompanies.push({ ...company, branches: [branch] });
         }
         
         // Open that company on the right so you can see the moved branch
         this.expandedAssignedCompanies = [company.id];
      },
      getGenderLabel(value) {
         const option = this.sexOptions.find(
            option => Number(option.value) === Number(value)
         );

         return option ? option.label : value;
      },

      getRoleLabel(value) {
         const option = this.rolesOptions.find(
            option => Number(option.id) === Number(value)
         );

         return option ? option.role : value;
      },

      // Alt+M, double click and Edit all open the company & branch access screen.
editRecord(record) {
    this.openCompanyBranchAccess(record);
},
   openCompanyBranchAccess(record) {
          this.isEditing = true;
          this.selectedAccessUserId = record.id ?? '';

          this.formInputs.id = record.id ?? '';
          this.formInputs.first_name = record.first_name ?? '';
          this.formInputs.middle_name = record.middle_name ?? '';
          this.formInputs.last_name = record.last_name ?? '';

          this.availableCompanies = [];
          this.assignedCompanies = [];

          this.availableSelectedBranches = {};
          this.assignedSelectedBranches = {};

          this.expandedAvailableCompanies = [];
          this.expandedAssignedCompanies = [];

          this.loadCompanyBranchAccess(record.id);
      },
      async loadCompanyBranchAccess(userId) {
          try {
            this.$showOrHideLoader("Loading...");
              /*this.handleAlert(
                  "starting",
                  "Loading company and branch access...",
                  "info",
                  "modal"
              );*/

              const url = `${this.apiUrl}?action=fetchUserCompanyBranchAccess&user_id=${userId}`;

              const response = await axios.get(
                  url,
                  this.$handleConfig()
              );

              if (response.data.status === "success") {

                  this.availableCompanies =
                      response.data.results.availableCompanies ?? [];

                  this.assignedCompanies =
                      response.data.results.assignedCompanies ?? [];

                  /*this.handleAlert(
                      "ending",
                      "",
                      "success",
                      "modal"
                  );*/
                  this.$showOrHideLoader("Loading...");

              } else {
                this.$showOrHideLoader("Loading...");
                  /*this.handleAlert(
                      "ending",
                      response.data.message,
                      "danger",
                      "modal"
                  );*/
              }

          } catch (error) {

              console.error(error);

              if (error.message === "Network Error") {
                  this.handleAlert(
                      "ending",
                      "No internet connection. Please check your network.",
                      "danger",
                      "modal"
                  );
              } else {
                  this.handleAlert(
                      "ending",
                      "Something went wrong. Please try again later.",
                      "danger",
                      "modal"
                  );
              }
          }
      },
      async saveOrEditRecord() {
         if (this.isSaving) return;
         this.isSaving = true;
          try {

              if (!this.selectedAccessUserId) {
                  this.submitted = true; // the User box turns red
                  this.$playErrorSound();
                  this.handleAlert("ending", "Please select a user first.", "danger", "modal");
                  return;
              }

            console.log(
                "Assigned branch IDs before save:",
                this.assignedCompanies.flatMap(company =>
                    company.branches.map(branch => Number(branch.id))
                )
            );
            this.$showOrHideLoader("Saving...");
              this.handleAlert(
                  "starting",
                  "Saving company and branch access...",
                  "info",
                  "modal"
              );

              const assignedBranchIds = [];

              this.assignedCompanies.forEach(company => {
                  company.branches.forEach(branch => {
                      assignedBranchIds.push(Number(branch.id));
                  });
              });

              console.log(
                  "Branch IDs being sent to PHP:",
                  assignedBranchIds
              );

              /*
               * A user must have at least one assigned branch.
               */
              if (assignedBranchIds.length === 0) {
                  this.$playErrorSound();
                  this.handleAlert(
                      "ending",
                      "Please assign at least one company and branch to this user.",
                      "danger",
                      "modal"
                  );
                  this.isSaving = false;
                  setTimeout(() => { this.$showOrHideLoader("Saving...");}, 500);
                  return;
              }

              const formData = new FormData();

              formData.append("action", "saveCompanyBranchAccess");
              formData.append("user_id", this.selectedAccessUserId);

              assignedBranchIds.forEach(branchId => {
                  formData.append("assigned_branch_ids[]", branchId);
              });

              const response = await axios.post(
                  this.apiUrl,
                  formData,
                  this.$handleConfig()
              );
              //alert(response.data.status);
              if (response.data.status === "success") {
                  this.$showOrHideLoader("Done...");
                  this.handleAlert(
                      "ending",
                      response.data.message,
                      "success",
                      "modal"
                  );

                  this.availableSelectedBranches = {};
                  this.assignedSelectedBranches = {};

                  await this.loadCompanyBranchAccess(
                      this.selectedAccessUserId
                  );
                  await this.fetchRecords(); // the table's "X branches · Y companies" changes too
                  this.flashRow(this.selectedAccessUserId); // that user's row blinks

              } else {
                this.$showOrHideLoader("Done...");
                  this.handleAlert(
                      "ending",
                      response.data.message,
                      "danger",
                      "modal"
                  );
              }

              } catch (error) {
              console.error(error);

              if (error.message === "Network Error") {
                  this.handleAlert(
                      "ending",
                      "No internet connection. Please check your network.",
                      "danger",
                      "modal"
                  );
              } else {
                  this.handleAlert(
                      "ending",
                      "Something went wrong. Please try again later.",
                      "danger",
                      "modal"
                  );
              }
          } finally {
              // Allow saving again, whatever happened above.
              this.isSaving = false;
          }
      },
   },
}; 
</script>